import { resolutionDataTs } from "@/pages/newHome/components/paint-dalle-textarea/paint-dalle-textarea-type";
import { Payload } from "@/types/type";
import { ImagePaintDrawType, NanoBananaImageSize } from "@/constants/image-paint";
import type {
  PaintContextMode,
} from "@/types/paint-reference";

export interface PaintDalleEditSource {
  taskId: string;
  sourceOutputId?: string;
  outputIndex?: number;
  imageUrl: string;
  conversationId: string;
  drawType: ImagePaintDrawType;
}

export interface PaintDalleUploadedEditSource {
  clientId?: string;
  uploadImageId?: string;
  ossKey: string;
  imageUrl: string;
  source: number;
  size?: number;
  drawType: ImagePaintDrawType;
  roleId?: string;
  roleLabel?: string;
  sourceType?: "UPLOAD" | "SYSTEM_PRESET";
  presetDisplayId?: string;
  presetIndex?: number;
  pendingRegistration?: boolean;
}

export interface PaintDalle {
  refreshLeftSidebar: boolean;
  conversationConfigId: string;
  dalleCurrentHasGenerate: boolean;
  dalleTextareaTxt: string;
  dalleCurrentPaintItem: object;
  dalleHistoryFirstId: string;
  logoTemplateInfo: {
    templatePrompt: string;
    img: string;
    originOssKey: string;
  };
  logoTemporaryStorage: {
    logoRequire: string;
    logoContent: string;
    selectLogoType: number;
  };
  resolutionItem: resolutionDataTs | null;
  nanoBananaImageSize: NanoBananaImageSize;
  configSettingId: string;
  activeDrawType: ImagePaintDrawType;
  editSource: PaintDalleEditSource | null;
  contextPrimarySource: PaintDalleEditSource | null;
  uploadedEditSources: PaintDalleUploadedEditSource[];
  /**
   * 兼容尚未迁移的调用方；始终等于 uploadedEditSources[0]。
   */
  uploadedEditSource: PaintDalleUploadedEditSource | null;
  contextMode: PaintContextMode;
  contextRevision?: string;
  contextParentTaskId?: string;
  pendingTaskId?: string;
}

const getUploadedEditSources = (
  state: PaintDalle,
): PaintDalleUploadedEditSource[] => {
  if (Array.isArray(state.uploadedEditSources)) {
    return state.uploadedEditSources;
  }
  return state.uploadedEditSource ? [state.uploadedEditSource] : [];
};

const normalizeUploadedEditSources = (
  sources: PaintDalleUploadedEditSource[],
) => {
  const ossKeys = new Set<string>();
  return sources
    .filter((source) => {
      if (!source?.ossKey || ossKeys.has(source.ossKey)) return false;
      ossKeys.add(source.ossKey);
      return true;
    })
    .slice(0, 4);
};

export default {
  state: {
    // 是否刷新左侧边栏
    refreshLeftSidebar: false,
    // 出图设置id
    conversationConfigId: "",
    // 当前最新的消息是否已经生成图片
    dalleCurrentHasGenerate: false,
    // dalle文本框内容
    dalleTextareaTxt: "",
    // dalle当前选中的绘画列表对象
    dalleCurrentPaintItem: {},
    // dalle历史第一条的id
    dalleHistoryFirstId: "",
    //模板信息
    logoTemplateInfo: {
      templatePrompt: "",
      img: "",
      originOssKey: "",
    },
    //临时存储
    logoTemporaryStorage: {
      logoRequire: "",
      logoContent: "",
      selectLogoType: 0,
    },
    resolutionItem: null,
    nanoBananaImageSize: "1K",
    configSettingId: "",
    activeDrawType: "gpt_image_2",
    editSource: null,
    contextPrimarySource: null,
    uploadedEditSources: [],
    uploadedEditSource: null,
    contextMode: "NEW",
    contextRevision: undefined,
    contextParentTaskId: undefined,
    pendingTaskId: undefined,
  } as PaintDalle,
  reducers: {
    setRefreshLeftSidebar: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        refreshLeftSidebar: payload,
      };
    },
    setResolutionItem: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        resolutionItem: payload,
      };
    },
    setNanoBananaImageSize: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        nanoBananaImageSize: ["1K", "2K", "4K"].includes(payload)
          ? payload
          : "1K",
      };
    },
    setConfigSettingId: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        configSettingId: payload,
      };
    },
    setActiveDrawType: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        activeDrawType: payload,
      };
    },
    setEditSource: (state: PaintDalle, { payload }: Payload) => {
      const uploadedEditSources = getUploadedEditSources(state);
      return {
        ...state,
        editSource: payload,
        contextPrimarySource: payload,
        uploadedEditSources,
        uploadedEditSource: uploadedEditSources[0] || null,
        // 点击历史图片属于显式编辑，不依赖“自动继承上一轮”开关。
        // 服务端 reference-context 恢复的自动上下文由 setReferenceContext
        // 单独设置为 CONTINUE。
        contextMode: "NEW",
        contextParentTaskId: undefined,
      };
    },
    setUploadedEditSource: (state: PaintDalle, { payload }: Payload) => {
      const uploadedEditSources = payload ? [payload] : [];
      return {
        ...state,
        uploadedEditSources,
        uploadedEditSource: uploadedEditSources[0] || null,
      };
    },
    setUploadedEditSources: (state: PaintDalle, { payload }: Payload) => {
      const uploadedEditSources = Array.isArray(payload)
        ? normalizeUploadedEditSources(payload)
        : [];
      return {
        ...state,
        uploadedEditSources,
        uploadedEditSource: uploadedEditSources[0] || null,
      };
    },
    addUploadedEditSource: (state: PaintDalle, { payload }: Payload) => {
      const currentSources = getUploadedEditSources(state);
      if (
        !payload?.ossKey ||
        currentSources.some(
          (item) => item.ossKey === payload.ossKey,
        ) ||
        currentSources.length >= 4
      ) {
        return state;
      }
      const uploadedEditSources = [
        ...currentSources,
        payload,
      ];
      return {
        ...state,
        uploadedEditSources,
        uploadedEditSource: uploadedEditSources[0],
      };
    },
    removeUploadedEditSource: (
      state: PaintDalle,
      { payload }: Payload,
    ) => {
      const uploadedEditSources = getUploadedEditSources(state).filter(
        (item) => item.ossKey !== payload,
      );
      return {
        ...state,
        uploadedEditSources,
        uploadedEditSource: uploadedEditSources[0] || null,
      };
    },
    moveUploadedEditSource: (state: PaintDalle, { payload }: Payload) => {
      const currentSources = getUploadedEditSources(state);
      const fromIndex = Number(payload?.fromIndex);
      const toIndex = Number(payload?.toIndex);
      if (
        !Number.isInteger(fromIndex) ||
        !Number.isInteger(toIndex) ||
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= currentSources.length ||
        toIndex >= currentSources.length
      ) {
        return state;
      }
      const uploadedEditSources = [...currentSources];
      const [moved] = uploadedEditSources.splice(fromIndex, 1);
      uploadedEditSources.splice(toIndex, 0, moved);
      return {
        ...state,
        uploadedEditSources,
        uploadedEditSource: uploadedEditSources[0] || null,
      };
    },
    setContextMode: (state: PaintDalle, { payload }: Payload) => {
      if (payload === "NEW") {
        return {
          ...state,
          contextMode: payload,
          editSource: null,
          contextParentTaskId: undefined,
        };
      }
      const editSource =
        state.editSource || state.contextPrimarySource;
      return {
        ...state,
        contextMode: payload,
        editSource,
        contextParentTaskId:
          editSource?.taskId || state.contextParentTaskId,
      };
    },
    setReferenceContext: (
      state: PaintDalle,
      { payload }: Payload,
    ) => {
      const primaryOutput = payload?.primaryOutput;
      const contextPrimarySource = primaryOutput
        ? {
            taskId: primaryOutput.sourceTaskId,
            sourceOutputId: primaryOutput.outputId,
            outputIndex: primaryOutput.outputIndex,
            imageUrl: primaryOutput.imageUrl,
            conversationId:
              payload.conversationId ||
              state.contextPrimarySource?.conversationId ||
              "",
            drawType: payload.drawType || state.activeDrawType,
          }
        : null;
      const continueMode = payload?.contextMode === "CONTINUE";
      return {
        ...state,
        contextMode: payload?.contextMode || "NEW",
        contextRevision: payload?.revision,
        contextParentTaskId: continueMode
          ? primaryOutput?.sourceTaskId
          : undefined,
        pendingTaskId: payload?.pendingTaskId || undefined,
        contextPrimarySource,
        editSource: continueMode ? contextPrimarySource : null,
      };
    },
    setPendingTaskId: (state: PaintDalle, { payload }: Payload) => ({
      ...state,
      pendingTaskId: payload || undefined,
    }),
    resetGenerationState: (state: PaintDalle) => ({
      ...state,
      dalleCurrentHasGenerate: false,
      pendingTaskId: undefined,
    }),
    setConversationConfigId: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        conversationConfigId: payload,
      };
    },

    setDalleCurrentHasGenerate: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        dalleCurrentHasGenerate: payload,
      };
    },

    setDalleTextareaTxt: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        dalleTextareaTxt: payload,
      };
    },

    setDalleCurrentPaintItem: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        dalleCurrentPaintItem: payload,
      };
    },

    setDalleHistoryFirstId: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        dalleHistoryFirstId: payload,
      };
    },
    setLogoTemplateInfo: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        logoTemplateInfo: payload,
      };
    },
    setLogoTemporaryStorage: (state: PaintDalle, { payload }: Payload) => {
      return {
        ...state,
        logoTemporaryStorage: payload,
      };
    },
  },
  effects: {},
};
